import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft,
  FiCheck, FiEye, FiFileText, FiPrinter, FiXCircle
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd,
  LineInput, RemoveLineBtn, TotalsSection, TotalsTable, TotalRow, SortableTh, SortContext
} from '../../components/SharedStyles';
import { getItemsCached, getEmployeesCached, getJobsCached, getLocationsCached, getVendorsCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import { missingLocation, reqLabel, LOCATION_REQUIRED_MSG } from '../../utils/locationRequired';
import { formatAmount } from '../../utils/documentNumbering';
import {
  createTransaction, updateTransaction, deleteTransaction,
  getTransactions, updateTransactionStatus
} from '../../utils/transactionHelper';
import { deleteStockFlow, getAvailableBatches, getAvailableSerials, getItemStockCached, invalidateItemStock } from '../../utils/stockFlowHelper';
import LineUnitPicker from '../../components/LineUnitPicker';
import { seedLineUnitFromItem, applyUnitChangeToLine } from '../../utils/unitConversion';
import { deleteSubTransactions } from '../../utils/subTransactionHelper';
import PageLayout from '../../components/PageLayout';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import { useLocation } from 'react-router-dom';
import useDocListPagination from '../../hooks/useDocListPagination';
import DocListPagination from '../../components/DocListPagination';
import ItemSelect from '../../components/ItemSelect';
import { checkStockAvailability, isFutureDate, toBaseQty } from '../../utils/validationHelpers';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import SerialSelectPopover from '../../components/SerialSelectPopover';

const TRAN_TYPE_CODE = 'MIP';

// Footer summary spec — aggregated server-side in paged mode,
// computed from the loaded list in filtered mode. Matches the old footer.
const SUMMARY_SPEC = {
  statuses: ['Draft', 'Posted']
};

const EMPTY_LINE = { itemId: '', description: '', quantity: 1, unitOfMeasure: '', unitCode: '', unitCost: 0, lineTotal: 0, trackingType: 'None', batchNumber: '', serialNumbers: [] };

const STATUS_COLORS = {
  Draft: colors.warning.main,
  Posted: colors.success.main,
  Cancelled: colors.error.main,
};

// Derived production-receipt progress (NOT the transaction status — that stays
// Posted so accounting/stock show in reports). Computed from linked PRC receipts.
const RECEIPT_BADGE = {
  Open: colors.info?.main || '#0288d1',
  'Partially Received': colors.warning.main,
  'Fully Received': colors.success.main,
};

const MaterialIssue = () => {
  const { activeCompany, currentUser, activeFinancialYear, activeBranch } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('store', 'materialIssue');
  const location = useLocation();
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [viewMode, setViewMode] = useState('list');
  const [receipts, setReceipts] = useState([]); // all PRC receipts (to derive received progress)
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Paginated list (2026-07-16, Firebase cost): 20 docs/page from Firestore.
  // Any search / status filter / custom column sort switches to a one-time
  // full active-year load so filtering behaves exactly as before.
  const listFiltering = searchQuery.trim() !== '' || statusFilter !== 'All'
    || sort.sortField !== 'docDate' || sort.sortDir !== 'desc';
  const {
    docs: records,
    loading,
    refresh: loadRecords,
    ensureDoc,
    applyLocal,
    summary: listSummary,
    pagination
  } = useDocListPagination({
    tranTypeCode: TRAN_TYPE_CODE,
    fullMode: listFiltering,
    summarySpec: SUMMARY_SPEC
  });

  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    docNumber: '', docDate: new Date().toISOString().split('T')[0],
    notes: '', status: 'Draft',
    employeeId: '', employeeName: '', jobId: '', jobName: '',
    locationId: '', locationName: '',
    // In-house vs outsourced production. When outsourced, a vendor is chosen here;
    // the vendor's processing charge is entered later on the Production Receipt.
    productionType: 'inhouse', vendorId: '', vendorName: ''
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);
  const [items, setItems] = useState([]);
  const [availableBatches, setAvailableBatches] = useState({});
  const [availableSerials, setAvailableSerials] = useState({});
  const [employees, setEmployees] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [stockBalances, setStockBalances] = useState({});
  const [printConfig, setPrintConfig] = useState(null);
  // Job-cost hint (2026-08-31): itemId -> weighted-avg cost of THIS job's own
  // purchases. Display/pre-fill only — valuation is untouched.
  const [jobCostMap, setJobCostMap] = useState({});
  const jobCostCacheRef = useRef({}); // jobId -> map, so re-picking a job costs 0 extra reads

  // The record list itself loads inside useDocListPagination above
  // (loadRecords = its refresh). The old full-history getTransactions load lived here.

  // Load PRC receipts so the list can show how much of each issue has been received.
  const loadReceipts = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const data = await getTransactions(activeCompany.id, { tranTypeCode: 'PRC' });
      setReceipts(data.filter(r => r.status !== 'Cancelled'));
    } catch (error) { console.error('Error loading receipts:', error); }
  }, [activeCompany]);

  const loadItems = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Tier 1: items come from the device-local master cache (0 Firestore reads/open).
      const rows = await getItemsCached(activeCompany.id);
      setItems(rows.filter(i => i.isDeleted !== true).sort((a, b) => (a.itemName || '').localeCompare(b.itemName || '')));
    } catch (error) {
      console.error('Error loading items:', error);
    }
  }, [activeCompany]);

  const loadEmployees = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getEmployeesCached(activeCompany.id);
      setEmployees(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading employees:', error); }
  }, [activeCompany]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getJobsCached(activeCompany.id);
      setJobs(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading jobs:', error); }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getLocationsCached(activeCompany.id);
      setLocations(rows.filter(r => r.isActive === true));
    } catch (error) { console.error('Error loading locations:', error); }
  }, [activeCompany]);

  // ── Job-cost hint ──────────────────────────────────────────────────────────
  // When a Job is selected, issue the item at the cost it was PURCHASED FOR THAT
  // JOB (weighted average of the job's own PI/NPI/IPI lines), not the item's
  // standing average. Read-cheap: one jobId-equality query (Firestore auto
  // single-field index — no composite index needed), cached per job for the form.
  const PURCHASE_TRAN_TYPES = useMemo(() => new Set(['PI', 'NPI', 'IPI']), []);
  const loadJobCosts = useCallback(async (jobId) => {
    if (!activeCompany?.id || !jobId) { setJobCostMap({}); return {}; }
    if (jobCostCacheRef.current[jobId]) { setJobCostMap(jobCostCacheRef.current[jobId]); return jobCostCacheRef.current[jobId]; }
    try {
      const txns = await getTransactions(activeCompany.id, { jobId });
      const agg = {}; // itemId -> { qty, value }
      txns.forEach(t => {
        if (!PURCHASE_TRAN_TYPES.has(t.tranTypeCode) || t.status === 'Cancelled') return;
        (t.lines || []).forEach(l => {
          if (!l.itemId) return;
          const qty = Number(l.quantity) || 0;
          const price = Number(l.unitPrice != null ? l.unitPrice : l.unitCost) || 0;
          if (qty <= 0) return;
          if (!agg[l.itemId]) agg[l.itemId] = { qty: 0, value: 0 };
          agg[l.itemId].qty += qty;
          agg[l.itemId].value += qty * price;
        });
      });
      const map = {};
      Object.keys(agg).forEach(id => { if (agg[id].qty > 0) map[id] = Math.round((agg[id].value / agg[id].qty) * 100) / 100; });
      jobCostCacheRef.current[jobId] = map;
      setJobCostMap(map);
      return map;
    } catch (error) {
      console.error('Error loading job costs:', error);
      setJobCostMap({});
      return {};
    }
  }, [activeCompany?.id, PURCHASE_TRAN_TYPES]);

  // Re-apply job cost to lines already on the grid (used when the user switches Job).
  // Reverts an auto-applied job cost back to the standing cost when the new job has
  // no purchase for that item — but never clobbers a manually-typed cost.
  const applyJobCostToLines = useCallback((map) => {
    setLineItems(prev => prev.map(line => {
      if (!line.itemId) return line;
      const item = items.find(i => i.id === line.itemId);
      const std = item ? seedLineUnitFromItem(item, { priceField: 'purchasePrice', defaultUnitField: 'defaultPurchaseUnit' }).unitCost : line.stdCost;
      const jc = map ? map[line.itemId] : undefined;
      if (jc == null) {
        return { ...line, unitCost: (line.jobCostApplied && std != null) ? std : line.unitCost, stdCost: std, jobCostApplied: false };
      }
      return { ...line, unitCost: jc, stdCost: std, jobCostApplied: true };
    }));
  }, [items]);

  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const rows = await getVendorsCached(activeCompany.id);
      setVendors((rows || []).filter(r => r.isDeleted !== true).sort((a, b) => (a.vendorName || a.companyName || '').localeCompare(b.vendorName || b.companyName || '')));
    } catch (error) { console.error('Error loading vendors:', error); }
  }, [activeCompany]);

  // Firebase cost: stock is fetched ONE ITEM AT A TIME, on demand. The old
  // mount-time load scanned the company's ENTIRE stockFlow history just to draw
  // badges the user may never look at. Now the dropdown shows a "stock?" chip
  // and this runs only when the user taps it (or when the availability check
  // needs an item it doesn't already have).
  const requestItemStock = useCallback(async (itemId) => {
    if (!activeCompany?.id || !itemId) return;
    try {
      const qty = await getItemStockCached(activeCompany.id, itemId, {
        branchId: (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : undefined
      });
      setStockBalances(prev => ({ ...prev, [itemId]: qty }));
    } catch (e) { console.error('Error loading item stock:', e); }
  }, [activeCompany, activeBranch]);

  // NOTE: the record list itself loads inside useDocListPagination — not here.
  useEffect(() => { loadReceipts(); loadItems(); loadEmployees(); loadJobs(); loadLocations(); loadVendors(); loadDocumentFormats(activeCompany.id).then(setPrintConfig); }, [loadReceipts, loadItems, loadEmployees, loadJobs, loadLocations, loadVendors]);

  // Keep the job-cost map in step with the selected Job (for the hint badge).
  // Load ONLY — never re-applies to lines here, so opening an existing issue
  // never rewrites its saved costs. Applying happens on user Job-change / item-add.
  useEffect(() => {
    if (formData.jobId) loadJobCosts(formData.jobId);
    else setJobCostMap({});
  }, [formData.jobId, loadJobCosts]);

  // Map issueId -> total received cost (from linked PRC receipts).
  const receivedByIssue = useMemo(() => {
    const map = {};
    receipts.forEach(r => {
      if (r.parentId) map[r.parentId] = (map[r.parentId] || 0) + (Number(r.totalValue) || 0);
    });
    return map;
  }, [receipts]);

  const receiptProgress = (rec) => {
    if (rec.status !== 'Posted') return null;
    const issued = Number(rec.totalValue) || 0;
    const received = receivedByIssue[rec.id] || 0;
    if (received <= 0) return 'Open';
    if (received + 0.01 < issued) return 'Partially Received';
    return 'Fully Received';
  };

  const filteredRecords = useMemo(() => {
    let filtered = records;
    if (statusFilter !== 'All') filtered = filtered.filter(r => r.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.docNumber?.toLowerCase().includes(q) || r.notes?.toLowerCase().includes(q)
      );
    }
    return sort.sortItems(filtered);
  }, [records, statusFilter, searchQuery, sort]);

  const calculateLine = (line) => ({
    ...line,
    unitPrice: line.unitCost || 0,
    lineTotal: Math.round(line.quantity * line.unitCost * 100) / 100
  });

  const totals = useMemo(() => {
    const lines = lineItems.map(calculateLine);
    return { totalValue: Math.round(lines.reduce((s, l) => s + l.lineTotal, 0) * 100) / 100 };
  }, [lineItems]);

  const handleHeaderChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'unitCode' && updated[index].itemId) {
        const item = items.find(i => i.id === updated[index].itemId);
        if (item) {
          const patch = applyUnitChangeToLine(item, value, { priceField: 'purchasePrice' });
          updated[index] = { ...updated[index], unitCode: patch.unitCode, unitOfMeasure: patch.unitOfMeasure, unitCost: patch.unitCost };
        }
      }
      if (field === 'itemId' && value) {
        const item = items.find(i => i.id === value);
        if (item) {
          updated[index].description = item.itemName || '';
          const seeded = seedLineUnitFromItem(item, { priceField: 'purchasePrice', defaultUnitField: 'defaultPurchaseUnit' });
          updated[index] = { ...updated[index], unitCode: seeded.unitCode, unitOfMeasure: seeded.unitOfMeasure, unitCost: seeded.unitCost, stdCost: seeded.unitCost, jobCostApplied: false };
          // Job-cost hint: if a Job is selected and this item was purchased against
          // it, override the standing cost with that job's own purchase cost.
          const jc = formData.jobId ? jobCostMap[value] : undefined;
          if (jc != null) {
            updated[index].unitCost = jc;
            updated[index].jobCostApplied = true;
          }
          updated[index].trackingType = item.trackingType || 'None';
          updated[index].batchNumber = '';
          updated[index].serialNumbers = [];
          if (item.trackingType === 'Batch') {
            getAvailableBatches(activeCompany.id, value).then(batches => {
              setAvailableBatches(prev => ({ ...prev, [value]: batches }));
              if (batches.length > 0) {
                setLineItems(prev => {
                  const u = [...prev];
                  if (u[index] && u[index].itemId === value) u[index] = { ...u[index], batchNumber: batches[0].batchNumber };
                  return u;
                });
              }
            });
          } else if (item.trackingType === 'Serial') {
            getAvailableSerials(activeCompany.id, value).then(serials => {
              setAvailableSerials(prev => ({ ...prev, [value]: serials }));
            });
          }
        }
      }
      updated[index] = calculateLine(updated[index]);
      return updated;
    });
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);

  const lineItemRefs = useRef([]);
  const pendingFocusRowRef = useRef(null);
  useEffect(() => {
    if (pendingFocusRowRef.current == null) return;
    const rowIdx = pendingFocusRowRef.current;
    pendingFocusRowRef.current = null;
    setTimeout(() => {
      const el = lineItemRefs.current[rowIdx];
      if (el && typeof el.focus === 'function') el.focus();
    }, 50);
  }, [lineItems.length]);

  const handleLastFieldEnter = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const isLastRow = index === lineItems.length - 1;
    const line = lineItems[index];
    const hasData = !!line.itemId || (Number(line.quantity) > 0);
    if (!hasData) return;
    if (isLastRow) {
      if (window.confirm('Add a new line item?')) { pendingFocusRowRef.current = lineItems.length; addLine(); }
    } else {
      const next = lineItemRefs.current[index + 1];
      if (next && typeof next.focus === 'function') next.focus();
    }
  };
  const removeLine = (index) => { if (lineItems.length <= 1) return; setLineItems(prev => prev.filter((_, i) => i !== index)); };

  const resetForm = () => {
    setFormData({ docNumber: '', docDate: new Date().toISOString().split('T')[0], notes: '', status: 'Draft', employeeId: '', employeeName: '', jobId: '', jobName: '', locationId: '', locationName: '', productionType: 'inhouse', vendorId: '', vendorName: '' });
    setLineItems([{ ...EMPTY_LINE }]);
    setEditingRecord(null);
  };

  const handleCreate = () => { resetForm(); setViewMode('form'); };
  const handleEdit = (rec) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setEditingRecord(rec);
    setFormData({ docNumber: rec.docNumber || '', docDate: rec.docDate || new Date().toISOString().split('T')[0], notes: rec.notes || '', status: rec.status || 'Draft', employeeId: rec.employeeId || '', employeeName: rec.employeeName || '', jobId: rec.jobId || '', jobName: rec.jobName || '', locationId: rec.locationId || '', locationName: rec.locationName || '', productionType: rec.productionType || 'inhouse', vendorId: rec.vendorId || '', vendorName: rec.vendorName || '' });
    setLineItems(rec.lines?.length > 0 ? rec.lines.map(l => ({ ...EMPTY_LINE, ...l })) : [{ ...EMPTY_LINE }]);
    setViewMode('form');
  };
  const handleView = (rec) => { setViewingRecord(rec); setViewMode('view'); };

  // Handle navigation from reports. With pagination the target may not be in
  // the loaded page — ensureDoc fetches that single document by id when needed.
  useEffect(() => {
    const targetId = location.state?.viewTransactionId;
    if (!targetId) return;
    ensureDoc(targetId).then(rec => {
      if (rec) {
        setViewingRecord(rec);
        setViewMode('view');
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title);
      }
    });
  }, [location.state, ensureDoc]);

  const handleSave = async (saveStatus) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (missingLocation(activeCompany, formData.locationId)) { alert(LOCATION_REQUIRED_MSG); return; }
    if (!activeFinancialYear?.id) { alert('No active financial year'); return; }
    const validLines = lineItems.filter(l => l.itemId && l.quantity > 0);
    if (validLines.length === 0) { alert('Please add at least one material with quantity'); return; }
    if (formData.productionType === 'outsource' && !formData.vendorId) { alert('Please select the outsource vendor'); return; }

    for (const line of validLines) {
      if (line.trackingType === 'Batch' && !line.batchNumber?.trim()) {
        alert(`Batch selection is required for "${line.itemName || line.description}"`); return;
      }
      if (line.trackingType === 'Serial') {
        const serials = line.serialNumbers || [];
        const qty = line.quantity || 0;
        if (serials.length !== qty) {
          alert(`"${line.itemName || line.description}" requires exactly ${qty} serial number(s). You selected ${serials.length}.`); return;
        }
      }
    }

    if (isFutureDate(formData.docDate)) {
      if (!window.confirm('Document date is in the future. Continue?')) return;
    }

    // Stock availability check when posting (warn but allow negative)
    if (saveStatus === 'Posted') {
      const stockItems = validLines.filter(l => l.itemId).map(l => ({ itemId: l.itemId, itemName: l.itemName || l.description, quantity: l.quantity, unitCode: l.unitCode || l.unitOfMeasure }));
      if (stockItems.length > 0) {
        // Firebase cost: reuse the on-screen stock badges + cached items.
        const itemsById = {};
        items.forEach(i => { itemsById[i.id] = i; });
        const insufficient = await checkStockAvailability(activeCompany.id, stockItems, { stockMap: stockBalances, itemsMap: itemsById, branchId: (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : null });
        if (insufficient.length > 0) {
          const msg = insufficient.map(i => `${i.itemName}: available ${i.available} ${i.unit || ''}, requested ${i.requested} ${i.unit || ''}`).join('\n');
          if (!window.confirm(`Insufficient stock (inventory will go negative):\n${msg}\n\nContinue anyway?`)) return;
        }
      }
    }

    setSaving(true);
    try {
      const calculatedLines = validLines.map((l, i) => {
        const item = items.find(it => it.id === l.itemId);
        return { ...calculateLine(l), lineNumber: i + 1, itemCode: item?.itemCode || '', itemName: item?.itemName || l.description || '' };
      });
      const status = saveStatus || 'Draft';
      const data = {
        tranTypeCode: TRAN_TYPE_CODE,
        financialYearId: activeFinancialYear.id,
        docDate: formData.docDate, notes: formData.notes, status,
        employeeId: formData.employeeId, employeeName: formData.employeeName,
        jobId: formData.jobId, jobName: formData.jobName,
        locationId: formData.locationId, locationName: formData.locationName,
        // In-house vs outsourced; vendor carried to the Production Receipt for charges.
        productionType: formData.productionType || 'inhouse',
        vendorId: formData.productionType === 'outsource' ? formData.vendorId : '',
        vendorName: formData.productionType === 'outsource' ? formData.vendorName : '',
        totalValue: totals.totalValue, lines: calculatedLines,
        branchId: activeBranch?.id || null, branchName: activeBranch?.branchName || null
      };

      let savedDocId = editingRecord?.id;
      let newTranData = null;
      if (editingRecord) {
        await updateTransaction(activeCompany.id, editingRecord.id, data, currentUser.uid);
      } else {
        newTranData = await createTransaction(activeCompany.id, data, currentUser.uid);
        savedDocId = newTranData.id;
      }

      // Firebase cost: keep the in-memory stock badges correct after an issue that
      // NEWLY reduced stock, so the next issue this session checks fresh numbers.
      if (status === 'Posted' && editingRecord?.status !== 'Posted') {
        setStockBalances(prev => {
          const next = { ...prev };
          calculatedLines.forEach(l => {
            if (!l.itemId) return;
            const it = items.find(i => i.id === l.itemId);
            if (it?.itemType === 'service') return;
            // On-demand stock: only adjust items whose balance we actually know.
            // Decrementing an unknown item would invent a bogus (negative) figure
            // that the next availability check would trust — leave it unknown and
            // let it be fetched fresh when needed.
            if (next[l.itemId] === undefined) return;
            const { requestedBase } = toBaseQty(it || {}, l.quantity, l.unitCode || l.unitOfMeasure);
            next[l.itemId] = Number(next[l.itemId]) - requestedBase;
          });
          return next;
        });
      }

      const savedRecord = editingRecord
        ? { ...editingRecord, ...data, id: savedDocId, status }
        : { ...newTranData, status };
      applyLocal(prev => editingRecord
        ? prev.map(r => r.id === savedDocId ? savedRecord : r)
        : [savedRecord, ...prev]);

      // Stock moved — drop the per-item cached quantities so the next "stock?"
      // lookup (here or on any other form this session) reads fresh numbers.
      invalidateItemStock(activeCompany.id);

      setViewMode('list');
      resetForm();
      loadRecords().catch(err => console.error('Background refresh failed:', err));
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rec) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if ((rec.childIds || []).length > 0) { alert('This issue has production receipts linked to it. Cancel those receipts first.'); return; }
    if (!window.confirm(`Delete ${rec.docNumber}?`)) return;
    try {
      await deleteTransaction(activeCompany.id, rec.id);
      await loadRecords();
    } catch (error) { console.error('Error deleting:', error); alert('Error deleting record'); }
  };

  const handleCancel = async (rec) => {
    if (!canEdit) { alert('You do not have permission to cancel.'); return; }
    if (rec.status === 'Cancelled') { alert('Already cancelled'); return; }
    if ((rec.childIds || []).length > 0) { alert('This issue has production receipts linked to it. Cancel those receipts first.'); return; }
    if (rec.status === 'Posted') {
      if (!window.confirm('This will reverse the stock and accounting (WIP) entries. Continue?')) return;
    }
    if (!window.confirm(`Cancel ${rec.docNumber}?`)) return;
    try {
      if (rec.status === 'Posted') {
        await deleteStockFlow(activeCompany.id, rec.id);
        await deleteSubTransactions(activeCompany.id, rec.id);
      }
      await updateTransactionStatus(activeCompany.id, rec.id, 'Cancelled', currentUser.uid);
      await loadRecords();
    } catch (error) { console.error('Error cancelling:', error); alert('Error cancelling record'); }
  };

  const handlePrint = (rec) => {
    const data = rec || viewingRecord;
    if (!data) return;
    printDocument('materialIssue', data, activeCompany, printConfig, activeBranch);
  };

  const renderListView = () => (
    <>
      <ActionBar>
        <SearchBox><FiSearch />
          <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </SearchBox>
        <FilterGroup>
          <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Posted">Posted</option>
            <option value="Cancelled">Cancelled</option>
          </StatusFilterSelect>
          {canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New Material Issue</TranAddButton>}
        </FilterGroup>
      </ActionBar>
      {loading ? <LoadingMessage>Loading...</LoadingMessage> : filteredRecords.length === 0 ? (
        <TranEmptyState><FiFileText size={48} /><h3>No Material Issues</h3>
          <p>{records.length === 0 ? 'Issue raw materials to production to get started' : 'No records match filters'}</p>
          {records.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create Material Issue</TranAddButton>}
        </TranEmptyState>
      ) : (
        <TableContainer><SortContext.Provider value={sort.ctxValue}><Table><thead><tr>
          <SortableTh field="docNumber">Doc No</SortableTh><SortableTh field="docDate">Date</SortableTh>{showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}<SortableTh field="locationName">Location</SortableTh>
          <SortableTh field="totalValue" style={{ textAlign: 'right' }}>Material Cost</SortableTh>
          <Th style={{ textAlign: 'center' }}>Received</Th>
          <SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh>
          <Th style={{ textAlign: 'center' }}>Actions</Th>
        </tr></thead><tbody>
          {filteredRecords.map(rec => {
            const prog = receiptProgress(rec);
            return (
            <Tr key={rec.id}>
              <Td><strong>{rec.docNumber}</strong></Td><Td>{rec.docDate}</Td>
              {showBranchCol && <Td>{rec.branchName || '-'}</Td>}
              <Td>{rec.locationName || '-'}</Td>
              <Td style={{ textAlign: 'right', fontWeight: 500 }}>{activeCompany?.currencyCode || 'PKR'} {formatAmount(rec.totalValue)}</Td>
              <Td style={{ textAlign: 'center' }}>{prog ? <StatusBadge $color={RECEIPT_BADGE[prog]}>{prog}</StatusBadge> : '-'}</Td>
              <Td style={{ textAlign: 'center' }}><StatusBadge $color={STATUS_COLORS[rec.status]}>{rec.status}</StatusBadge></Td>
              <Td style={{ textAlign: 'center' }}><ActionGroup>
                <IconBtn title="View" onClick={() => handleView(rec)}><FiEye /></IconBtn>
                {canChange && <IconBtn title="Edit" onClick={() => handleEdit(rec)}><FiEdit2 /></IconBtn>}
                <IconBtn title="Print" onClick={() => handlePrint(rec)}><FiPrinter /></IconBtn>
                {canDelete && <IconBtn title="Delete" $danger onClick={() => handleDelete(rec)}><FiTrash2 /></IconBtn>}
                {canEdit && rec.status !== 'Cancelled' && (
                  <IconBtn title="Cancel" onClick={() => handleCancel(rec)} style={{ color: colors.error.main }}><FiXCircle /></IconBtn>
                )}
              </ActionGroup></Td>
            </Tr>
          ); })}
        </tbody></Table></SortContext.Provider></TableContainer>
      )}
      <DocListPagination {...pagination} />
      <SummaryBar>
        <span>Total: <strong>{listFiltering ? filteredRecords.length : pagination.totalCount}</strong> record(s)</span>
        <span>Draft: <strong>{listSummary.counts.Draft || 0}</strong> | Posted: <strong>{listSummary.counts.Posted || 0}</strong></span>
      </SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingRecord) return null;
    const rec = viewingRecord;
    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); setViewingRecord(null); }}><FiArrowLeft /> Back to List</BackButton>
          <FormTitle>{rec.docNumber}<StatusBadge $color={STATUS_COLORS[rec.status]} style={{ marginLeft: 12 }}>{rec.status}</StatusBadge></FormTitle>
          <FormActions>
            {canChange && <SaveButton onClick={() => handleEdit(rec)}><FiEdit2 /> Edit</SaveButton>}
            <SaveButton $secondary onClick={() => handlePrint(rec)}><FiPrinter /> Print</SaveButton>
          </FormActions>
        </FormHeader>
        <FormCard>
          <SectionTitle>Issue Details</SectionTitle>
          <InfoGrid>
            <InfoItem><label>Doc No</label><span>{rec.docNumber}</span></InfoItem>
            <InfoItem><label>Date</label><span>{rec.docDate}</span></InfoItem>
            <InfoItem><label>From Location</label><span>{rec.locationName || '-'}</span></InfoItem>
            <InfoItem><label>Employee</label><span>{rec.employeeName || '-'}</span></InfoItem>
            <InfoItem><label>Job</label><span>{rec.jobName || '-'}</span></InfoItem>
            <InfoItem><label>Production</label><span>{rec.productionType === 'outsource' ? 'Outsource' : 'In-house'}</span></InfoItem>
            {rec.productionType === 'outsource' && <InfoItem><label>Vendor</label><span>{rec.vendorName || '-'}</span></InfoItem>}
            <InfoItem><label>Material Cost</label><span>{activeCompany?.currencyCode || 'PKR'} {formatAmount(rec.totalValue)}</span></InfoItem>
          </InfoGrid>
        </FormCard>
        <FormCard>
          <SectionTitle>Materials Issued</SectionTitle>
          <TableContainer><Table><thead><tr>
            <Th>#</Th><Th>Description</Th><Th style={{ textAlign: 'right' }}>Qty</Th>
            <Th style={{ textAlign: 'right' }}>Unit Cost</Th><Th style={{ textAlign: 'right' }}>Total</Th>
          </tr></thead><tbody>
            {(rec.lines || []).map((line, i) => (
              <Tr key={i}><Td>{i + 1}</Td><Td>{line.description}</Td>
                <Td style={{ textAlign: 'right' }}>{line.quantity}{line.unitOfMeasure ? ` ${line.unitOfMeasure}` : ''}</Td>
                <Td style={{ textAlign: 'right' }}>{formatAmount(line.unitCost)}</Td>
                <Td style={{ textAlign: 'right' }}>{formatAmount(line.lineTotal)}</Td>
              </Tr>
            ))}
          </tbody></Table></TableContainer>
          <TotalsSection><TotalsTable><tbody>
            <TotalRow><td>Total Material Cost ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(rec.totalValue)}</td></TotalRow>
          </tbody></TotalsTable></TotalsSection>
        </FormCard>
        {rec.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{rec.notes}</NotesText></FormCard>}
      </>
    );
  };

  const renderFormView = () => (
    <>
      <FormHeader>
        <BackButton onClick={() => { setViewMode('list'); resetForm(); }}><FiArrowLeft /> Back to List</BackButton>
        <FormTitle>{editingRecord ? `Edit: ${editingRecord.docNumber}` : 'New Material Issue to Production'}</FormTitle>
        <FormActions>
          <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}><FiX /> Cancel</SaveButton>
          {canEdit && <SaveButton onClick={() => handleSave('Draft')} disabled={saving}><FiSave /> {saving ? 'Saving...' : 'Save Draft'}</SaveButton>}
          {canEdit && (formData.status === 'Draft' || !editingRecord) && (
            <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving}><FiCheck /> Save & Post</SaveButton>
          )}
        </FormActions>
      </FormHeader>
      <FormCard>
        <SectionTitle>Issue Details</SectionTitle>
        <FormGrid>
          <FormGroup><Label>Doc No</Label><Input value={editingRecord?.docNumber || 'Auto-generated'} disabled /></FormGroup>
          <FormGroup><Label>Date *</Label><Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} /></FormGroup>
          <FormGroup><Label>Status</Label><Input value={formData.status} disabled /></FormGroup>
          <FormGroup><Label>{reqLabel(activeCompany, 'From Location')}</Label>
            <Select value={formData.locationId} onChange={e => { const loc = locations.find(l => l.id === e.target.value); handleHeaderChange('locationId', e.target.value); handleHeaderChange('locationName', loc ? (loc.name || loc.locationName || '') : ''); }}>
              <option value="">-- Select Location --</option>
              {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name || loc.locationName || loc.id}</option>)}
            </Select>
          </FormGroup>
          <FormGroup><Label>Employee</Label>
            <Select value={formData.employeeId} onChange={e => { const emp = employees.find(em => em.id === e.target.value); handleHeaderChange('employeeId', e.target.value); handleHeaderChange('employeeName', emp ? (emp.fullName || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.employeeName || '') : ''); }}>
              <option value="">-- Select Employee --</option>
              {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.fullName || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.employeeName || emp.id}</option>)}
            </Select>
          </FormGroup>
          <FormGroup><Label>Job</Label>
            <Select value={formData.jobId} onChange={async e => { const jid = e.target.value; const job = jobs.find(j => j.id === jid); handleHeaderChange('jobId', jid); handleHeaderChange('jobName', job ? (job.name || job.jobName || '') : ''); const map = await loadJobCosts(jid); applyJobCostToLines(map); }}>
              <option value="">-- Select Job --</option>
              {jobs.map(job => <option key={job.id} value={job.id}>{job.name || job.jobName || job.id}</option>)}
            </Select>
          </FormGroup>
          <FormGroup><Label>Production Type</Label>
            <Select value={formData.productionType} onChange={e => { const v = e.target.value; setFormData(prev => ({ ...prev, productionType: v, ...(v === 'inhouse' ? { vendorId: '', vendorName: '' } : {}) })); }}>
              <option value="inhouse">In-house</option>
              <option value="outsource">Outsource (Vendor)</option>
            </Select>
          </FormGroup>
          {formData.productionType === 'outsource' && (
            <FormGroup><Label>Outsource Vendor *</Label>
              <Select value={formData.vendorId} onChange={e => { const v = vendors.find(x => x.id === e.target.value); handleHeaderChange('vendorId', e.target.value); handleHeaderChange('vendorName', v ? (v.vendorName || v.companyName || '') : ''); }}>
                <option value="">-- Select Vendor --</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.vendorCode ? `${v.vendorCode} — ` : ''}{v.vendorName || v.companyName || v.id}</option>)}
              </Select>
            </FormGroup>
          )}
        </FormGrid>
      </FormCard>
      <FormCard>
        <LineItemsHeader>
          <SectionTitle style={{ margin: 0 }}>Materials</SectionTitle>
          <AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton>
        </LineItemsHeader>
        <LineTableContainer><LineTable><thead><tr>
          <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}>#</LineTh>
          <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Item</LineTh>
          <LineTh style={{ width: 260, minWidth: 260, maxWidth: 260 }}>Description</LineTh>
          <LineTh style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Qty</LineTh>
          <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Unit Cost</LineTh>
          <LineTh style={{ width: 140, minWidth: 140, maxWidth: 140 }}>Total</LineTh>
          {lineItems.some(l => l.trackingType === 'Batch') && <LineTh style={{ width: 160, minWidth: 160, maxWidth: 160 }}>Batch</LineTh>}
          {lineItems.some(l => l.trackingType === 'Serial') && <LineTh style={{ width: 220, minWidth: 220, maxWidth: 220 }}>Serial Numbers</LineTh>}
          <LineTh style={{ width: 50, minWidth: 50, maxWidth: 50 }}></LineTh>
        </tr></thead><tbody>
          {lineItems.map((line, index) => {
            const calc = calculateLine(line);
            return (
              <tr key={index}>
                <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
                <LineTd><ItemSelect ref={(el) => { lineItemRefs.current[index] = el; }} items={items} selectedItemId={line.itemId} onChange={(itemId) => handleLineChange(index, 'itemId', itemId)} stockBalances={stockBalances} stockOnDemand onRequestStock={requestItemStock} /></LineTd>
                <LineTd><LineInput value={line.description} onChange={e => handleLineChange(index, 'description', e.target.value)} placeholder="Description" /></LineTd>
                <LineTd><div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><LineInput type="number" min="0" value={line.quantity} onChange={e => handleLineChange(index, 'quantity', Number(e.target.value))} style={{ textAlign: 'right' }} /><LineUnitPicker item={items.find(i => i.id === line.itemId)} line={line} onChange={(uc) => handleLineChange(index, 'unitCode', uc)} /></div></LineTd>
                <LineTd>
                  <LineInput type="number" min="0" step="0.01" value={line.unitCost} onChange={e => handleLineChange(index, 'unitCost', Number(e.target.value))} onKeyDown={(e) => handleLastFieldEnter(e, index)} style={{ textAlign: 'right' }} />
                  {formData.jobId && line.itemId && line.jobCostApplied && (
                    <div style={{ fontSize: '10px', color: '#2e7d32', textAlign: 'right', marginTop: '2px' }} title="Cost taken from this job's own purchases (editable)">
                      job cost{line.stdCost != null && line.stdCost !== line.unitCost ? ` · std ${formatAmount(line.stdCost)}` : ''}
                    </div>
                  )}
                </LineTd>
                <LineTd style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(calc.lineTotal)}</LineTd>
                {lineItems.some(l => l.trackingType === 'Batch') && (
                  <LineTd>{line.trackingType === 'Batch' ? (
                    <select value={line.batchNumber} onChange={e => handleLineChange(index, 'batchNumber', e.target.value)} style={{ padding: '6px 8px', fontSize: '13px', minWidth: 120, border: '1px solid #ddd', borderRadius: '4px' }}>
                      <option value="">Select Batch</option>
                      {(availableBatches[line.itemId] || []).map(b => (<option key={b.batchNumber} value={b.batchNumber}>{b.batchNumber} (Qty: {b.remaining})</option>))}
                    </select>
                  ) : <span style={{ color: '#999' }}>-</span>}</LineTd>
                )}
                {lineItems.some(l => l.trackingType === 'Serial') && (
                  <LineTd>{line.trackingType === 'Serial' ? (
                    <SerialSelectPopover availableSerials={availableSerials[line.itemId] || []} selectedSerials={line.serialNumbers || []} requiredCount={line.quantity || 0} onChange={(serials) => handleLineChange(index, 'serialNumbers', serials)} />
                  ) : <span style={{ color: '#999' }}>-</span>}</LineTd>
                )}
                <LineTd style={{ textAlign: 'center' }}>{lineItems.length > 1 && <RemoveLineBtn onClick={() => removeLine(index)}><FiX /></RemoveLineBtn>}</LineTd>
              </tr>
            );
          })}
        </tbody></LineTable></LineTableContainer>
        <TotalsSection><TotalsTable><tbody>
          <TotalRow><td>Total Material Cost ({activeCompany?.currencyCode || 'PKR'})</td><td>{formatAmount(totals.totalValue)}</td></TotalRow>
        </tbody></TotalsTable></TotalsSection>
      </FormCard>
      <FormCard>
        <SectionTitle>Notes</SectionTitle>
        <FormGrid><FormGroup $span2><TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Additional notes..." rows={3} /></FormGroup></FormGrid>
      </FormCard>
    </>
  );

  useDocumentShortcuts({
    onNew: () => { if (!canEdit) { alert('You do not have permission to create.'); return; } handleCreate(); },
    onSave: () => { if (!canEdit) { alert('You do not have permission to save changes.'); return; } handleSave('Posted'); },
    viewMode, saving
  });

  return (
    <PageLayout pageTitle="Material Issue to Production" breadcrumb="Store / Material Issue">
      {viewMode === 'list' && renderListView()}
      {viewMode === 'form' && renderFormView()}
      {viewMode === 'view' && renderViewMode()}
    </PageLayout>
  );
};

export default MaterialIssue;
